import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthProvider } from "@/lib/AuthContext";
import { SiteProvider } from "@/lib/SiteContext";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import SiteSchema from "@/components/site/SiteSchema";
import CookieBanner from "@/components/site/CookieBanner";
import MobileActionBar, { showsActionBar } from "@/components/site/MobileActionBar";
import { captureClickIds, trackPageView } from "@/lib/analytics";
import { Loading, PageMeta } from "@/components/site/Bits";
import Home from "@/pages/Home";
import { AuthCallback } from "@/pages/Auth";
import { Toaster } from "@/components/ui/sonner";

// Every route below the homepage is split into its own bundle, so a visitor only
// downloads the code for the page they are actually reading.
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const AdviceCentre = lazy(() => import("@/pages/Advice").then((m) => ({ default: m.AdviceCentre })));
const ArticleDetail = lazy(() => import("@/pages/Advice").then((m) => ({ default: m.ArticleDetail })));
const About = lazy(() => import("@/pages/Static").then((m) => ({ default: m.About })));
const Contact = lazy(() => import("@/pages/Static").then((m) => ({ default: m.Contact })));
const OnlineTools = lazy(() => import("@/pages/Static").then((m) => ({ default: m.OnlineTools })));
const Surveys = lazy(() => import("@/pages/Booking").then((m) => ({ default: m.Surveys })));
const BookInspection = lazy(() => import("@/pages/Booking").then((m) => ({ default: m.BookInspection })));
const DampChecker = lazy(() => import("@/pages/tools/DampChecker"));
const ReportReview = lazy(() => import("@/pages/tools/ReportReview"));
const Estimate = lazy(() => import("@/pages/tools/Estimate"));
const AdminPortalShell = lazy(() => import("@/pages/adminnew/Shell"));
const PreviewHome = lazy(() => import("@/pages/adminnew/Home"));
const PreviewRecords = lazy(() => import("@/pages/adminnew/Records"));
const PreviewJob = lazy(() => import("@/pages/adminnew/Job"));
const PreviewCalendar = lazy(() => import("@/pages/adminnew/Calendar"));
const PreviewFinance = lazy(() => import("@/pages/adminnew/Finance"));
const PreviewMessages = lazy(() => import("@/pages/adminnew/Messages"));
const PreviewMore = lazy(() => import("@/pages/adminnew/More"));
const PreviewHealth = lazy(() => import("@/pages/adminnew/Health"));
const SurveyReports = lazy(() => import("@/pages/adminnew/SurveyReports"));
const AdminNotifications = lazy(() => import("@/pages/adminnew/Notifications"));
const PreviewParity = lazy(() => import("@/pages/adminnew/Parity"));
const PreviewNewEnquiry = lazy(() => import("@/pages/adminnew/NewEnquiry"));
const PreviewBookSurvey = lazy(() => import("@/pages/adminnew/BookSurvey"));
const PreviewNewQuotation = lazy(() => import("@/pages/adminnew/NewQuotation"));
const PreviewQuotation = lazy(() => import("@/pages/adminnew/Quotation"));
const PreviewNewReport = lazy(() => import("@/pages/adminnew/NewReport"));
const PreviewNewInvoice = lazy(() => import("@/pages/adminnew/NewInvoice"));
const PreviewBookWorks = lazy(() => import("@/pages/adminnew/BookWorks"));
const PreviewWorkRecords = lazy(() => import("@/pages/adminnew/WorkRecords"));
const PortalSettings = lazy(() => import("@/pages/adminnew/Settings"));
const ArchivedRecords = lazy(() => import("@/pages/adminnew/Archived"));
const PreviewExceptions = lazy(() => import("@/pages/adminnew/Exceptions"));
const FailedCommunications = lazy(() => import("@/pages/adminnew/Communications"));
const AdminPrototype = lazy(() => import("@/pages/adminnew/Prototype"));
const AdminErasure = lazy(() => import("@/pages/adminnew/Erasure"));
const ExportEverything = lazy(() => import("@/pages/adminnew/ExportEverything"));
const ForgotPassword = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.ResetPassword })));
const StaffLogin = lazy(() => import("@/pages/StaffLogin"));
const AreasHub = lazy(() => import("@/pages/areas/AreasHub"));
const CountyHub = lazy(() => import("@/pages/areas/CountyHub"));
const LocationPage = lazy(() => import("@/pages/areas/LocationPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancelled = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancelled })));
const PrivacyPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.PrivacyPolicy })));
const Reviews = lazy(() => import("@/pages/Reviews"));
const PaymentPlans = lazy(() => import("@/pages/PaymentPlans"));
const TechnicianLogin = lazy(() => import("@/pages/technician/TechnicianLogin"));
const TechnicianPortal = lazy(() => import("@/pages/technician/TechnicianPortal"));
const SurveyorLogin = lazy(() => import("@/pages/surveyor/SurveyorLogin"));
const SurveyorPortal = lazy(() => import("@/pages/surveyor/SurveyorPortal"));
const CookiePolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.CookiePolicy })));

const NotFound = () => (
  <div className="pg-container py-24 text-center">
    <PageMeta title="Page not found | Procure Group" robots="noindex, follow"
              description="The page you were looking for is not available." />
    <h1 className="text-3xl font-bold text-pg-dark">Page not found</h1>
    <p className="mt-3 text-sm text-pg-muted">
      The page you were looking for is not available. Please use the navigation above.
    </p>
  </div>
);

function LegacyAreaRedirect() {
  const { slug } = useParams();
  const [path, setPath] = useState("");

  useEffect(() => {
    api
      .get(`/areas-resolve/${slug}`)
      .then(({ data }) => setPath(data.path))
      .catch(() => setPath("/areas-covered"));
  }, [slug]);

  if (!path) return <Loading label="Redirecting" />;
  return <Navigate to={path} replace />;
}


/** /service-areas/anything keeps whatever came after it and moves to /areas-covered. */
function ServiceAreasRedirect() {
  const { pathname, search } = useLocation();
  const tail = pathname.replace(/^\/service-areas/, "");
  return <Navigate to={`/areas-covered${tail}${search}`} replace />;
}


function Shell() {
  const location = useLocation();
  const bare = location.pathname.startsWith("/admin") || location.pathname === "/staff"
    || location.pathname.startsWith("/technician")
    || location.pathname.startsWith("/surveyor");

  // One Google Ads page_view per route change, and the advert click id kept for the journey.
  useEffect(() => {
    captureClickIds(location.search);
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      {!bare && <SiteHeader />}
      <main className={!bare ? "pg-site" : undefined}>
        <Suspense fallback={<Loading label="Loading" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            {["bathroom-ventilation", "kitchen-ventilation", "positive-input-ventilation",
              "heat-recovery-ventilation", "subfloor-ventilation"].map((slug) => (
              <Route key={slug} path={`/services/${slug}`}
                     element={<Navigate to="/services/condensation" replace />} />
            ))}
            <Route path="/services/pre-purchase-damp-assessments"
                   element={<Navigate to="/services/damp-investigations" replace />} />
            {/* Structural waterproofing withdrawn as a service, June 2026. */}
            {["basement-waterproofing", "structural-waterproofing", "cavity-drainage-membrane",
              "cementitious-tanking"].map((slug) => (
              <Route key={slug} path={`/services/${slug}`}
                     element={<Navigate to="/services/damp-investigations" replace />} />
            ))}
            <Route path="/online-tools" element={<OnlineTools />} />
            <Route path="/tools/check-my-damp" element={<DampChecker />} />
            <Route path="/tools/upload-my-survey" element={<ReportReview />} />
            <Route path="/tools/get-an-estimate" element={<Estimate />} />
            <Route path="/surveys" element={<Surveys />} />
            <Route path="/book-an-inspection" element={<BookInspection />} />
            <Route path="/advice" element={<AdviceCentre />} />
            <Route path="/advice/:slug" element={<ArticleDetail />} />
            <Route path="/advice/ventilation-and-indoor-moisture"
                   element={<Navigate to="/advice/how-to-reduce-condensation" replace />} />
            <Route path="/advice/air-bricks-and-subfloor-ventilation"
                   element={<Navigate to="/advice/air-bricks-and-subfloor-airflow" replace />} />
            {["basement-waterproofing-assessment", "why-basements-leak"].map((slug) => (
              <Route key={slug} path={`/advice/${slug}`}
                     element={<Navigate to="/services/damp-investigations" replace />} />
            ))}
            <Route path="/about" element={<About />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/payment-plans" element={<PaymentPlans />} />
            {/* Landlord, agent and housing provider platform temporarily withdrawn. */}
            <Route path="/landlords-and-managing-agents" element={<Navigate to="/contact" replace />} />
            <Route path="/landlord-register" element={<Navigate to="/contact" replace />} />
            <Route path="/portfolio" element={<Navigate to="/contact" replace />} />
            <Route path="/report-a-concern" element={<Navigate to="/contact" replace />} />
            <Route path="/areas-covered" element={<AreasHub />} />
            <Route path="/areas-covered/:county" element={<CountyHub />} />
            <Route path="/areas-covered/:county/:town" element={<LocationPage />} />
            <Route path="/areas" element={<Navigate to="/areas-covered" replace />} />
            <Route path="/areas/:slug" element={<LegacyAreaRedirect />} />
            {/* Never our own URL structure, but it gets typed and linked, so it lands properly. */}
            <Route path="/service-areas" element={<Navigate to="/areas-covered" replace />} />
            <Route path="/service-areas/*" element={<ServiceAreasRedirect />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Navigate to="/contact" replace />} />
            <Route path="/landlord-login" element={<Navigate to="/contact" replace />} />
            <Route path="/staff" element={<StaffLogin />} />
            <Route path="/register" element={<Navigate to="/contact" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/activate" element={<Navigate to="/contact" replace />} />
            <Route path="/portal" element={<Navigate to="/contact" replace />} />
            <Route path="/portal/*" element={<Navigate to="/contact" replace />} />
            <Route path="/admin/new" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/new/*" element={<Navigate to="/admin" replace />} />
            <Route path="/admin" element={<AdminPortalShell />}>
              <Route index element={<PreviewHome />} />
              <Route path="records/:name" element={<PreviewRecords />} />
              <Route path="jobs/:customerId" element={<PreviewJob />} />
              <Route path="calendar" element={<PreviewCalendar />} />
              <Route path="finance" element={<PreviewFinance />} />
              <Route path="messages" element={<PreviewMessages />} />
              <Route path="more" element={<PreviewMore />} />
              <Route path="health" element={<PreviewHealth />} />
              <Route path="export" element={<ExportEverything />} />
              <Route path="survey-reports" element={<SurveyReports />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="parity" element={<PreviewParity />} />
              <Route path="enquiries/new" element={<PreviewNewEnquiry />} />
              <Route path="survey-bookings/new" element={<PreviewBookSurvey />} />
              <Route path="quotations/new" element={<PreviewNewQuotation />} />
              <Route path="quotations/:quotationId" element={<PreviewQuotation />} />
              <Route path="reports" element={<PreviewNewReport />} />
              <Route path="reports/new" element={<PreviewNewReport />} />
              <Route path="invoices/new" element={<PreviewNewInvoice />} />
              <Route path="works/new" element={<PreviewBookWorks />} />
              <Route path="work-records" element={<PreviewWorkRecords />} />
              <Route path="work-records/:projectId" element={<PreviewWorkRecords />} />
              <Route path="settings" element={<PortalSettings />} />
              <Route path="archived" element={<ArchivedRecords />} />
              <Route path="exceptions" element={<PreviewExceptions />} />
              <Route path="communications" element={<FailedCommunications />} />
              <Route path="prototype" element={<AdminPrototype />} />
              <Route path="erasure" element={<AdminErasure />} />
            </Route>
            {/* Private field application — deliberately not linked from anywhere public. */}
            <Route path="/technician/login" element={<TechnicianLogin />} />
            <Route path="/technician/*" element={<TechnicianPortal />} />
            <Route path="/surveyor/login" element={<SurveyorLogin />} />
            <Route path="/surveyor/*" element={<SurveyorPortal />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancelled />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!bare && <SiteFooter />}
      {/* The action bar already carries WhatsApp on a phone, so the floating button steps aside. */}
      {!bare && <WhatsAppButton desktopOnly={showsActionBar(location.pathname)} />}
      {!bare && <MobileActionBar />}
      {!bare && <SiteSchema />}
      {!bare && <CookieBanner />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
          <Shell />
          <Toaster />
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
